import React, { Component } from "react";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import moment from "moment";
import { roundHours } from "../../utils/entry";
import ReactTooltip from "react-tooltip";
import get from "lodash/get";
import times from "lodash/times";

// Component Imports
import DatesFields from "./DatesFields";
import InputField from "./InputTypes/InputField";
import RepeatField from "./InputTypes/RepeatField";

// SASS Imports
require("react-datepicker/dist/react-datepicker.css");

const _FORECASTING_STATE = {
  NONE: "none",
  ERROR: "error",
  ALLOWED: "allowed"
};

export default class DateInput extends Component {
  constructor(props) {
    super(props);

    this.state = DateInput.getDerivedStateFromProps(props);
  }

  static getDerivedStateFromProps(props) {
    const now = moment();

    const { allowForecasting } = props;

    if (allowForecasting) {
      return {
        startDateMax: null,
        endDateMax: now.clone().add(1, "month")
      };
    } else {
      return {
        startDateMax: now.clone().subtract(1, "day"),
        endDateMax: now.clone(),
      };
    }
  }

  setSingleDate(newDate) {
    if (!newDate) {
      return;
    }
    this.props.updateSingleDate(newDate);
  }

  setStartDate(startDate) {
    if (!startDate) {
      return;
    }

    const oneMonthAfterStart = startDate.clone().add(1, "month");

    let endDate = this.props.endDate.clone();

    // adjust end date, if necessary
    if (startDate > endDate) {
      endDate = startDate.clone().add(1, "day");
    }
    if (endDate > oneMonthAfterStart) {
      endDate = oneMonthAfterStart.clone();
    }

    this.props.updateMultiDate(startDate, endDate);

    // update end date cap
    let endDateMax = oneMonthAfterStart.clone();

    if (!this.props.allowForecasting && oneMonthAfterStart.isAfter()) {
      endDateMax = moment();
    }

    this.setState({ endDateMax });
  }

  setEndDate(newDate) {
    if (!newDate) {
      return;
    }

    const startDate = this.props.startDate > newDate ? newDate : this.props.startDate;
    const endDate = newDate;

    this.props.updateMultiDate(startDate, endDate);
  }

  _getForecastingState () {
    if (!this.props.allowForecasting) {
      if (this.props.singleView) {
        return (this.props.singleDate.isBefore())
          ? _FORECASTING_STATE.NONE
          : _FORECASTING_STATE.ERROR;
      }

      return (this.props.endDate.isBefore())
        ? _FORECASTING_STATE.NONE
        : _FORECASTING_STATE.ERROR;
    }

    return _FORECASTING_STATE.ALLOWED;
  }

  _renderForecastingHint () {
    const forecastState = this._getForecastingState();

    if (forecastState === _FORECASTING_STATE.NONE) {
      return null;
    }

    let message = "Forecasting permitted for FTO";
    let className = "";

    if (forecastState === _FORECASTING_STATE.ERROR) {
      message = "Forcasting is not allowed for this category";
      className = "forecast-warning";
    }

    return (
      <span>
        <a data-tip={message} className={className}>
          <small><span className="glyphicon glyphicon-time"></span></small>
        </a>
        <ReactTooltip type="info" place="right" multiline={true} />
      </span>
    );
  }

  _renderSingleView () {
    const pickerClasses = [ "dateInput" ];

    if (this._getForecastingState() === _FORECASTING_STATE.ERROR) {
      pickerClasses.push("has-error");
    }

    return (
      <div>
        <div className="col-sm-3 col-xs-6">
          <label>Date {this._renderForecastingHint()}</label>
          <DatePicker
            maxDate={(this.props.allowForecasting) ? null : moment()}
            selected={this.props.singleDate}
            onChange={(date) => this.setSingleDate(date)}
            popoverTargetOffset="10px 0"
            className={pickerClasses.join(" ")}
          />
        </div>
        <InputField
          hasError={this.props.requiredFields.indexOf("duration") >= 0}
          title="Duration"
          value={this.props.duration}
          placeholder="8.0"
          onChange={({ target }) => this.props.updateDuration(target.value)}
          onBlur={({ target }) => this.props.updateDuration(roundHours(target.value))}
          type="number"
          inputProps={{ step: 0.5, min: 0, max: 24 }}
          colClass="col-sm-3 col-xs-6"
        />
      </div>
    );
  }

  _renderMultiView() {
    const currentDate = this.props.startDate.clone();
    const startDatePickerClasses = ["dateInput"];
    const endDatePickerClasses = ["dateInput", "right-date"];

    if (this._getForecastingState() === _FORECASTING_STATE.ERROR) {
      startDatePickerClasses.push("has-error");
      endDatePickerClasses.push("has-error");
    }

    return (
      <div className="dateContainer">
        <div className="col-sm-3 col-xs-6">
          <label>Start Date</label>
          <DatePicker
            maxDate={this.state.startDateMax}
            selected={this.props.startDate}
            startDate={this.props.startDate}
            endDate={this.props.endDate}
            onChange={(date) => this.setStartDate(date)}
            popoverAttachment="top left"
            popoverTargetAttachment="bottom left"
            popoverTargetOffset="10px 0"
            className={startDatePickerClasses.join(" ")}
          />
        </div>
        <div className="col-sm-3 col-xs-6">
          <label>End Date {this._renderForecastingHint()}</label>
          <DatePicker
            minDate={this.props.startDate.clone().add(1, "days")}
            maxDate={this.state.endDateMax}
            selected={this.props.endDate}
            startDate={this.props.startDate}
            endDate={this.props.endDate}
            onChange={(date) => this.setEndDate(date)}
            popoverAttachment="top right"
            popoverTargetAttachment="bottom right"
            popoverTargetOffset="10px 0"
            className={endDatePickerClasses.join(" ")}
          />
        </div>
        <div className="col-xs-12"><hr /></div>
        <div className="row no-margin">
          <div className="col-sm-4 col-xs-6">
            <label>Dates</label>
          </div>
          <div className="col-sm-4 col-xs-6">
            <label>
              Duration (Hours: {this.props.totalDuration})
            </label>
          </div>
        </div>
        <DatesFields {...this.props} startDate={currentDate.toDate()} />
      </div>
    );
  }

  render () {
    if (this.props.singleView) {
      return this._renderSingleView();
    }

    return this._renderMultiView();
  }
}

DateInput.displayName = "TimeInput.DateInput";

DateInput.propTypes = {
  allowForecasting: PropTypes.bool,
  duration: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
  endDate: PropTypes.object.isRequired,
  requiredFields: PropTypes.array.isRequired,
  multiDayDurations: PropTypes.array.isRequired,
  multiDayTimeEntryDaySpan: PropTypes.number.isRequired,
  singleDate: PropTypes.object.isRequired,
  singleView: PropTypes.bool.isRequired,
  startDate: PropTypes.object.isRequired,
  totalDuration: PropTypes.number.isRequired,
  updateDuration: PropTypes.func.isRequired,
  updateDurationMulti: PropTypes.func.isRequired,
  updateMultiDate: PropTypes.func.isRequired,
  updateSingleDate: PropTypes.func.isRequired,
};

DateInput.defaultProps = { allowForecasting: true };



// WEBPACK FOOTER //
// ./app/components/TimeInput/DateInput.jsx